import { Box, Button, Group, MantineSize, Paper, Text } from '@mantine/core';
import { BaseRecord } from '@refinedev/core';
import { IconArrowLeft, IconArrowRight } from '@tabler/icons-react';
import { DataTable, DataTableColumn, DataTableSortStatus } from 'mantine-datatable';
import { ReactNode } from 'react';
import { SearchHeader } from './SearchHeader';
import TableTitleBar from './TableTitleBar';
import { PaginationData } from './hooks/useTable';

export type TableProps<T extends BaseRecord = BaseRecord> = {
  title?: string;
  subtitle?: string;
  titleIcon?: ReactNode;
  onTitleClick?: () => void;
  columns: DataTableColumn<T>[];
  data: T[];
  isLoading: boolean;
  onRowClick: (record: T) => void;
  searchTerm?: string;
  setSearchTerm?: (term: string) => void;
  HeaderRow?: ReactNode;
  variant?: 'default' | 'clients-table';
  showCount?: boolean;
  primaryButton?: {
    label: string;
    variant?: 'default' | 'primary';
    onClick: () => void;
  };
  pagination: PaginationData;
  totalRecords: number;
  sortStatus?: DataTableSortStatus<T>;
  onSortStatusChange?: (sortStatus: DataTableSortStatus<T>) => void;
  p?: MantineSize | number;
};

export function Table<T extends BaseRecord = BaseRecord>({
  title,
  subtitle,
  titleIcon,
  onTitleClick,
  columns,
  data,
  isLoading,
  onRowClick,
  searchTerm,
  setSearchTerm,
  HeaderRow,
  primaryButton,
  variant = 'default',
  showCount = false,
  pagination,
  totalRecords,
  sortStatus,
  onSortStatusChange,
  p = 0,
}: TableProps<T>) {
  return (
    <>
      <Paper variant="content" p={p}>
        {title && (
          <TableTitleBar
            title={title}
            subtitle={subtitle}
            titleIcon={titleIcon}
            onTitleClick={onTitleClick}
            totalRecords={totalRecords}
            showCount={showCount}
            primaryButton={primaryButton}
          />
        )}
        <>
          {HeaderRow && (
            <Group
              px="lg"
              py="sm"
              justify="space-between"
              style={{
                borderBottom: 'var(--solid-border)',
              }}
            >
              {HeaderRow}
            </Group>
          )}
          {searchTerm !== undefined && setSearchTerm && (
            <SearchHeader searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
          )}
        </>
        {sortStatus && onSortStatusChange ? (
          <DataTable<T>
            withTableBorder={false}
            withColumnBorders={false}
            withRowBorders
            borderColor="gray.2"
            rowBorderColor="gray.2"
            striped
            highlightOnHover
            columns={columns}
            records={data}
            fetching={isLoading}
            minHeight={200}
            variant={variant}
            sortStatus={sortStatus}
            onSortStatusChange={onSortStatusChange}
            onCellClick={({ record, column }) => {
              if (column.accessor === 'actions') {
                return;
              }
              onRowClick(record);
            }}
            styles={{
              header: {
                backgroundColor: 'var(--mantine-color-gray-0)',
                fontSize: 'var(--mantine-font-size-xs)',
                fontWeight: 600,
                color: 'var(--mantine-color-gray-6)',
              },
            }}
          />
        ) : (
          <DataTable<T>
            withTableBorder={false}
            withColumnBorders={false}
            withRowBorders
            borderColor="gray.2"
            rowBorderColor="gray.2"
            striped
            highlightOnHover
            columns={columns}
            records={data}
            fetching={isLoading}
            minHeight={200}
            variant={variant}
            onCellClick={({ record, column }) => {
              if (column.accessor === 'actions') {
                return;
              }
              onRowClick(record);
            }}
            styles={{
              header: {
                backgroundColor: 'var(--mantine-color-gray-0)',
                fontSize: 'var(--mantine-font-size-xs)',
                fontWeight: 600,
                color: 'var(--mantine-color-gray-6)',
              },
            }}
          />
        )}

        <Box px="lg" py="sm" style={{ borderTop: 'var(--solid-border)' }}>
          <Group justify="space-between">
            <Button
              variant="outline-black"
              leftSection={<IconArrowLeft size={16} />}
              disabled={pagination.active === 1}
              onClick={() => pagination.previous()}
            >
              Previous
            </Button>
            <Group gap="xs">
              {pagination.range.map((pageNum: number | 'dots', index: number) => {
                if (pageNum === 'dots') {
                  return (
                    <Text size="sm" fw={500} c="gray.10" key={index}>
                      ...
                    </Text>
                  );
                }
                return (
                  <Button
                    key={index}
                    variant="subtle"
                    data-active={pagination.active === pageNum}
                    w={40}
                    h={40}
                    p={0}
                    radius={8}
                    onClick={() => pagination.setPage(pageNum as number)}
                  >
                    <Text size="sm" fw={500} c="gray.10">
                      {pageNum}
                    </Text>
                  </Button>
                );
              })}
            </Group>
            <Button
              variant="outline-black"
              rightSection={<IconArrowRight size={16} />}
              disabled={pagination.active === pagination.range[pagination.range.length - 1]}
              onClick={() => pagination.next()}
            >
              Next
            </Button>
          </Group>
        </Box>
      </Paper>
    </>
  );
}
